import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, RefreshCw, Zap } from "lucide-react";

import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { contractingKeys } from "@/lib/query-keys";
import { useDepartments } from "@/hooks/queries/useDepartments";
import {
  describeOutdatedWorks,
  useQualificationAnalysis,
} from "@/hooks/useQualificationAnalysis";
import type {
  Department,
  MyCompanyDocumentsState,
  QualificationStatus,
} from "@/types/domain";

const CARD =
  "rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900";
const PRIMARY_BUTTON =
  "inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500";

/** "A", "A y B", "A, B y C". */
function joinTitles(titles: string[]): string {
  if (titles.length <= 1) return titles.join("");
  return `${titles.slice(0, -1).join(", ")} y ${titles.at(-1)}`;
}

const worksLabel = (count: number) => `${count} ${count === 1 ? "licitación" : "licitaciones"}`;

/** "a, b y 3 más": los primeros de una lista, para una sola línea de texto. */
function firstOf(values: string[], max: number): string {
  if (values.length <= max) return values.join(", ");
  return `${values.slice(0, max).join(", ")} y ${values.length - max} más`;
}

/** "PIURA · ítems: puente, pistas": lo que filtra la línea, para elegirla sin abrirla. */
function lineScopeLabel(
  line: QualificationStatus["lines"][number],
  departments: Department[],
): string {
  const zones = line.departmentIds
    .map((id) => departments.find((d) => d.department_id === id)?.department_name)
    .filter((name): name is string => Boolean(name));
  const zone = zones.length === 0 ? "Todo el Perú" : firstOf(zones, 2);
  const items =
    line.itemKeywords.length === 0 ? "sin ítems" : `ítems: ${firstOf(line.itemKeywords, 3)}`;
  return `${zone} · ${items}`;
}

/**
 * Lo que frena el análisis según los documentos: faltan esenciales o la IA todavía
 * los lee. También los opcionales que faltan, para avisar sin frenar.
 */
function documentsBlocker(documents: MyCompanyDocumentsState) {
  const allDocuments = [
    ...documents.slots.flatMap((slot) => slot.documents),
    ...documents.otherDocuments,
    ...documents.unreadDocuments,
  ];
  const readingCount = allDocuments.filter((doc) => doc.state === "PROCESSING").length;
  const emptySlots = documents.slots.filter((slot) => !slot.loaded);
  const missingRequired = emptySlots
    .filter((slot) => slot.essential)
    .map((slot) => slot.title);
  const missingOptional = emptySlots
    .filter((slot) => !slot.essential)
    .map((slot) => slot.title);
  const blockedReason =
    missingRequired.length > 0
      ? `Sube al menos ${joinTitles(missingRequired)} para analizar: sin ellas casi todas las licitaciones saldrían por revisar.`
      : readingCount > 0
        ? `Esperando que terminemos de leer ${readingCount} ${readingCount === 1 ? "documento" : "documentos"}…`
        : null;
  return { readingCount, missingRequired, missingOptional, blockedReason };
}

/** La barra de avance mientras corre un análisis. */
function AnalyzingCard({ done, total }: { done: number; total: number }) {
  const percent = Math.round((done / total) * 100);
  return (
    <div className={CARD}>
      <div className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
        <Loader2 className="size-4 animate-spin text-primary" />
        Analizando tus licitaciones
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {done} de {total} licitaciones. Puedes seguir usando Licitigo mientras tanto.
      </p>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className="h-full bg-primary transition-[width] duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
      <Link
        to="/dashboard"
        className="mt-3 inline-block cursor-pointer text-xs font-semibold text-primary hover:underline"
      >
        Ver el avance en Descubrimiento →
      </Link>
    </div>
  );
}

/** Las líneas con su casilla: solo se analizan las marcadas. */
function LinePicker({
  lines,
  checkedIds,
  onToggle,
  disabled,
}: {
  lines: QualificationStatus["lines"];
  checkedIds: number[];
  onToggle: (lineId: number) => void;
  disabled: boolean;
}) {
  const { data: departments = [] } = useDepartments();
  return (
    <div className="mt-5">
      <div className="text-xs font-bold text-slate-700 dark:text-slate-200">
        ¿En qué líneas postulas?
      </div>
      <p className="mt-0.5 text-[11px] text-slate-500">
        Deja sin marcar las que usas para venderle a quien gana la licitación.
      </p>
      <div className="mt-3 flex flex-col divide-y rounded-xl border border-slate-100 dark:divide-slate-800 dark:border-slate-800">
        {lines.map((line) => {
          const checked = checkedIds.includes(line.searchProfileId);
          // Una línea sin ítem o sin zona no se puede marcar; si ya lo estaba, sí se puede quitar.
          const locked = disabled || (line.blockedReason !== null && !checked);
          return (
            <label
              key={line.searchProfileId}
              className={cn(
                "flex items-center gap-3 px-4 py-2.5 text-sm",
                locked ? "cursor-default" : "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40",
              )}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => onToggle(line.searchProfileId)}
                disabled={locked}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-slate-700 dark:text-slate-200">
                  {line.name}
                </span>
                <span className="block truncate text-[11px] text-slate-400">
                  {lineScopeLabel(line, departments)}
                </span>
                {line.blockedReason ? (
                  <span className="block text-[11px] text-amber-700 dark:text-amber-400">
                    {line.blockedReason}
                  </span>
                ) : (
                  !line.onlyInterests && (
                    <span className="block text-[11px] text-slate-500">
                      Solo cruzamos las licitaciones que coinciden con sus ítems, no todo lo que
                      muestra la línea.
                    </span>
                  )
                )}
              </span>
              {!line.blockedReason && (
                <span className="shrink-0 text-xs tabular-nums text-slate-400">
                  {worksLabel(line.totalWorks)} abiertas
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * El cruce automático en Configuración → Documentos (Comercial y Corporativo): el único lugar
 * con el botón, porque lo que lo habilita son los documentos. Se activa por línea:
 * la primera vez se eligen y se cruzan; después las obras nuevas se analizan solas.
 */
export function AnalyzeWorksCard({ documents }: { documents: MyCompanyDocumentsState }) {
  const qc = useQueryClient();
  const analysis = useQualificationAnalysis();
  // Antes de activar, la elección es local: nada se guarda hasta apretar el botón.
  const [selectedLineIds, setSelectedLineIds] = useState<number[]>([]);

  const { readingCount, missingRequired, missingOptional, blockedReason } =
    documentsBlocker(documents);

  // Un documento recién leído cambia la ficha: los cruces hechos quedan desactualizados.
  useEffect(() => {
    if (readingCount === 0) {
      qc.invalidateQueries({ queryKey: contractingKeys.qualification() });
    }
  }, [readingCount, qc]);

  const onlyAdminsNote = !documents.canManage && (
    <p className="mt-4 text-xs text-slate-500">
      Solo los administradores de tu organización pueden lanzar el análisis.
    </p>
  );

  if (analysis.status === "ANALYZING") {
    return <AnalyzingCard done={analysis.progressDone} total={analysis.progressTotal} />;
  }

  if (analysis.status === "ACTIVE") {
    const lineCount = analysis.markedLineIds.length;
    return (
      <div className={CARD}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <CheckCircle2 className="size-8 shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
              Calificas en {analysis.qualifiedWorks} de {analysis.totalWorks} licitaciones
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              {missingRequired.length > 0
                ? `Análisis detenido: sube ${joinTitles(missingRequired)} para seguir analizando tus licitaciones.`
                : `Análisis activo en ${lineCount} ${lineCount === 1 ? "línea" : "líneas"}. Las licitaciones nuevas se analizan solas apenas leemos sus bases.`}
            </p>
          </div>
          <Link
            to="/dashboard"
            className="inline-flex shrink-0 cursor-pointer items-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Ver licitaciones
          </Link>
        </div>

        {analysis.outdatedWorks > 0 && (
          <div className="mt-5 rounded-xl bg-slate-50 p-4 dark:bg-slate-800/40">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <RefreshCw className="size-4 shrink-0 text-slate-500" />
              <p className="min-w-0 flex-1 text-xs font-medium text-slate-700 dark:text-slate-200">
                {describeOutdatedWorks(analysis)}.
              </p>
              {documents.canManage && (
                <button
                  type="button"
                  onClick={() => analysis.analyze()}
                  disabled={blockedReason !== null || analysis.isStarting}
                  className={PRIMARY_BUTTON}
                >
                  <Zap className="size-4" />
                  Actualizar {worksLabel(analysis.outdatedWorks)}
                </button>
              )}
            </div>
            {(blockedReason || analysis.staleWorks > 0) && (
              <p className="mt-2 text-[11px] text-slate-500">
                {blockedReason ??
                  "Las licitaciones que ya cerraron registro conservan su análisis."}
              </p>
            )}
          </div>
        )}

        <LinePicker
          lines={analysis.lines}
          checkedIds={analysis.markedLineIds}
          onToggle={analysis.toggleLine}
          disabled={!documents.canManage}
        />
        {onlyAdminsNote}
      </div>
    );
  }

  const selectedWorks = analysis.lines
    .filter((line) => selectedLineIds.includes(line.searchProfileId))
    .reduce((sum, line) => sum + line.totalWorks, 0);

  return (
    <div className={CARD}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary">
          <Zap className="size-5 text-primary-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            Analizar mis licitaciones
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            Cruzamos cada licitación con el registro abierto contra tus documentos y te
            decimos en cuáles calificas. Después, las licitaciones nuevas se analizan solas.
          </p>
        </div>
        {documents.canManage && (
          <button
            type="button"
            onClick={() => analysis.analyze(selectedLineIds)}
            // Sin licitaciones cruzables igual se activa: en bienes, marcar la línea es lo
            // que hace que se bajen sus bases.
            disabled={
              blockedReason !== null || selectedLineIds.length === 0 || analysis.isStarting
            }
            className={PRIMARY_BUTTON}
          >
            <Zap className="size-4" />
            {selectedLineIds.length === 0
              ? "Elige tus líneas"
              : selectedWorks === 0
                ? "Activar análisis"
                : `Analizar ${worksLabel(selectedWorks)}`}
          </button>
        )}
      </div>

      <LinePicker
        lines={analysis.lines}
        checkedIds={selectedLineIds}
        onToggle={(lineId) =>
          setSelectedLineIds((current) =>
            current.includes(lineId)
              ? current.filter((id) => id !== lineId)
              : [...current, lineId],
          )
        }
        disabled={!documents.canManage}
      />

      {(blockedReason || missingOptional.length > 0) && (
        <p
          className={cn(
            "mt-4 rounded-xl px-4 py-2.5 text-xs",
            blockedReason
              ? "bg-slate-50 text-slate-500 dark:bg-slate-800/40"
              : "bg-amber-50 text-amber-800 dark:bg-amber-950/20 dark:text-amber-300",
          )}
        >
          {blockedReason ??
            `Faltan ${joinTitles(missingOptional)}: puedes analizar igual, pero algunos factores saldrán por revisar.`}
        </p>
      )}
      {onlyAdminsNote}
    </div>
  );
}

/**
 * El análisis en Documentos para quien cruza al desbloquear: explica que cada obra
 * desbloqueada se analiza sola y ofrece "Analizar N" para las desbloqueadas que
 * siguen abiertas y no están al día (por ejemplo, si subió los documentos después).
 */
export function UnlockedWorksCard({ documents }: { documents: MyCompanyDocumentsState }) {
  const qc = useQueryClient();
  const analysis = useQualificationAnalysis();
  const { readingCount, blockedReason } = documentsBlocker(documents);

  // Un documento recién leído cambia la ficha: los cruces hechos quedan desactualizados.
  useEffect(() => {
    if (readingCount === 0) {
      qc.invalidateQueries({ queryKey: contractingKeys.qualification() });
    }
  }, [readingCount, qc]);

  if (analysis.status === "ANALYZING") {
    return <AnalyzingCard done={analysis.progressDone} total={analysis.progressTotal} />;
  }

  return (
    <div className={CARD}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary">
          <Zap className="size-5 text-primary-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            {analysis.totalWorks > 0
              ? `Calificas en ${analysis.qualifiedWorks} de ${worksLabel(analysis.totalWorks)} desbloqueadas`
              : "Cada licitación que desbloquees se analiza"}
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            Al desbloquear una licitación con el registro abierto la cruzamos contra estos
            documentos y te decimos si calificas.
          </p>
        </div>
      </div>

      {analysis.outdatedWorks > 0 && (
        <div className="mt-5 rounded-xl bg-slate-50 p-4 dark:bg-slate-800/40">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <RefreshCw className="size-4 shrink-0 text-slate-500" />
            <p className="min-w-0 flex-1 text-xs font-medium text-slate-700 dark:text-slate-200">
              {describeOutdatedWorks(analysis)}.
            </p>
            {documents.canManage && (
              <button
                type="button"
                onClick={() => analysis.analyze()}
                disabled={blockedReason !== null || analysis.isStarting}
                className={PRIMARY_BUTTON}
              >
                <Zap className="size-4" />
                Analizar {worksLabel(analysis.outdatedWorks)}
              </button>
            )}
          </div>
          {blockedReason && <p className="mt-2 text-[11px] text-slate-500">{blockedReason}</p>}
        </div>
      )}
    </div>
  );
}
